import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { FAQList } from '@/components/site/FAQ';
import { CTABand } from '@/components/site/CTABand';
import { FaqSchema } from '@/components/site/Seo';
import { WRAP, SECTION, H2, H2_SM } from '@/components/site/ServicePageParts';
import { SITE } from '@/data/site';
import { CITIES } from '@/data/cities';
import { PLANS, RETAINERS, MARKET, COST_FACTORS, PRICING_FAQ, PRICING_UPDATED } from '@/data/pricing';

const RULE = 'border-white/[0.08]';
const LINK_CLS = 'nd-link-u text-white';
const n2 = (i) => String(i + 1).padStart(2, '0');

function Head({ num, label, heading, intro }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-14 md:mb-20">
      <SectionLabel num={num} className="lg:pt-4">{label}</SectionLabel>
      <div>
        <Reveal><h2 className={`${H2} max-w-[20ch]`}>{heading}</h2></Reveal>
        {intro && <Reveal delay={100}><p className="mt-7 text-ink-muted text-[17px] leading-relaxed max-w-[60ch]">{intro}</p></Reveal>}
      </div>
    </div>
  );
}

function PricingHero() {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-[12vw] -top-[18vw] h-[70vw] w-[70vw] max-h-[1000px] max-w-[1000px] rounded-full blur-[20px]"
        style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.32), rgba(75,159,255,.12) 40%, transparent 68%)' }}
      />
      <div className={`relative ${WRAP} pt-16 md:pt-24 pb-20 md:pb-28`}>
        <nav aria-label="Breadcrumb" className="nd-mono flex flex-wrap items-center gap-2.5 !text-[11px]">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <span aria-hidden>/</span>
          <span className="text-white">Pricing</span>
        </nav>
        <SectionLabel as="h1" num="—" className="mt-12 md:mt-16" data-testid="pricing-h1">
          Website Development Cost in the US ({PRICING_UPDATED})
        </SectionLabel>
        <WordReveal
          as="p"
          text="Clear prices. Fixed quotes. No surprise invoices."
          accent="Fixed"
          className="mt-8 max-w-[15ch] font-display font-semibold text-[clamp(44px,7.4vw,128px)] leading-[0.92] tracking-[-0.05em]"
        />
        <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <Reveal delay={350} className="lg:col-span-6 lg:col-start-7 lg:row-start-1">
            <p className="text-lg md:text-xl text-[#C9C9D3] leading-relaxed max-w-[50ch]">
              What a website, online store or app costs with NioDelta — and how that compares with
              what US agencies publicly charge. All prices in US dollars,
              final. Every project gets a fixed written quote after a free discovery call.
            </p>
          </Reveal>
          <Reveal delay={450} className="lg:col-span-5 lg:row-start-1">
            <ul className={`border-t ${RULE}`}>
              {PLANS.map((p) => (
                <li key={p.slug} className={`border-b ${RULE}`}>
                  <Link to={`#plan-${p.slug}`} className="group flex items-baseline justify-between gap-4 py-2.5 text-[15px] text-[#C9C9D3] hover:text-white transition-colors">
                    <span>{p.name}</span>
                    <span className="nd-mono !text-[11px] tabular-nums group-hover:text-white transition-colors">{p.from}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/**
 * Tall tier column. Uses CSS subgrid so the header, price, feature list and
 * CTA of every card in a row share the same row tracks: feature lists start
 * at the same y and CTAs sit on one baseline.
 */
function PlanCard({ plan, index }) {
  const f = plan.featured;
  return (
    <Reveal
      delay={(index % 3) * 80}
      className="row-span-4 grid grid-rows-subgrid gap-y-0 mb-4 md:mb-5"
    >
      <article
        id={`plan-${plan.slug}`}
        data-testid={`plan-${plan.slug}`}
        className={`row-span-4 grid grid-rows-subgrid gap-y-0 scroll-mt-28 rounded-[18px] border p-7 md:p-8 ${
          f
            ? 'border-violet/70 bg-[linear-gradient(180deg,rgba(108,92,231,.16),rgba(108,92,231,.04))] shadow-[0_0_0_1px_rgba(108,92,231,.25),0_30px_80px_-40px_rgba(108,92,231,.6)]'
            : 'border-white/[0.1] bg-ink-surface/60'
        }`}
      >
        <div>
          <div className="flex items-center justify-between gap-4">
            <span className="nd-mono !text-[11px]">{n2(index)} / {String(PLANS.length).padStart(2, '0')}</span>
            {f && (
              <span className="nd-mono !text-[10px] !text-white inline-flex items-center gap-2 rounded-full border border-violet/60 bg-violet/20 px-2.5 py-1">
                <span className="nd-live" />Recommended
              </span>
            )}
          </div>
          <h3 className="mt-8 font-display text-[26px] md:text-[30px] font-semibold tracking-[-0.035em] leading-none">{plan.name}</h3>
          <div className="mt-3 nd-mono !text-[11px]">{plan.bestFor}</div>
        </div>

        <div className={`mt-8 pt-7 border-t ${f ? 'border-violet/30' : RULE}`}>
          <div className="nd-mono !text-[11px]">Starting at</div>
          <div className={`mt-2 font-display font-semibold text-[clamp(48px,4.6vw,76px)] leading-[0.9] tracking-[-0.06em] tabular-nums ${f ? 'nd-grad-text' : ''}`}>
            {plan.from}
          </div>
          <div className="mt-5 text-sm text-ink-muted leading-relaxed">
            Typical: <span className="text-[#C9C9D3] tabular-nums">{plan.range}</span> · {plan.timeline}
          </div>
        </div>

        <ul className={`mt-8 pt-7 border-t ${f ? 'border-violet/30' : RULE} space-y-3 text-[15px] text-white/85`}>
          {plan.includes.map((item) => (
            <li key={item} className="flex gap-3">
              <Check size={16} strokeWidth={1.75} className={`shrink-0 mt-[3px] ${f ? 'text-white' : 'text-[#8C7CFF]'}`} />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <div className="mt-10 self-end">
          {plan.link && (
            <Link
              to={plan.link}
              className={`group flex items-center justify-between gap-4 rounded-full border px-5 py-3.5 text-sm transition-colors duration-300 ${
                f ? 'border-white bg-white text-ink-bg hover:bg-transparent hover:text-white' : 'border-white/15 text-white hover:border-white hover:bg-white hover:text-ink-bg'
              }`}
            >
              About this service
              <span aria-hidden className="transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:translate-x-1">→</span>
            </Link>
          )}
        </div>
      </article>
    </Reveal>
  );
}

function Plans() {
  return (
    <section className={SECTION}>
      <div className={WRAP}>
        <Head num="01" label="Packages" heading="Website, ecommerce & app development prices" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 md:gap-x-5 gap-y-0">
          {PLANS.map((p, i) => <PlanCard key={p.slug} plan={p} index={i} />)}
        </div>
        <p className="mt-6 text-sm text-ink-muted">All prices in US dollars. No US sales tax is added — we are an Indian company and provide a W-8BEN-E on request.</p>
      </div>
    </section>
  );
}

function Retainers() {
  return (
    <section className={SECTION}>
      <div className={WRAP}>
        <Head num="02" label="After launch" heading="Website maintenance & support charges" />
        <div className={`grid grid-cols-1 md:grid-cols-3 border-t ${RULE}`}>
          {RETAINERS.map((r, i) => (
            <Reveal key={r.name} delay={i * 80} className={`border-b md:border-b-0 ${RULE} md:pr-8 ${i ? 'md:border-l md:pl-8' : ''}`}>
              <div className="h-full flex flex-col pt-8 pb-10">
                <span className="nd-mono !text-[11px]">{n2(i)}</span>
                <h3 className="mt-10 font-display text-2xl font-semibold tracking-[-0.03em]">{r.name}</h3>
                <div className="mt-4 font-display font-semibold text-[clamp(32px,3vw,48px)] leading-none tracking-[-0.05em] tabular-nums">{r.price}</div>
                <p className="mt-6 text-[15px] text-ink-muted leading-relaxed max-w-[40ch]">{r.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function MarketTable() {
  return (
    <section className={SECTION}>
      <div className={WRAP}>
        <Head num="03" label="Market comparison" heading="What US agencies charge" intro={MARKET.intro} />
        <Reveal>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-[15px] border-t border-white/[0.14]">
              <thead>
                <tr className={`border-b ${RULE}`}>
                  <th scope="col" className="nd-mono !text-[11px] text-left font-normal py-5 pr-6">Project</th>
                  <th scope="col" className="nd-mono !text-[11px] text-left font-normal py-5 pr-6">Budget / template</th>
                  <th scope="col" className="nd-mono !text-[11px] text-left font-normal py-5 pr-6">Mid-market agency</th>
                  <th scope="col" className="nd-mono !text-[11px] !text-white text-left font-normal py-5 px-5 bg-violet/[0.14] rounded-t-[12px]">NioDelta</th>
                </tr>
              </thead>
              <tbody>
                {MARKET.rows.map((r) => (
                  <tr key={r.project} className={`border-b ${RULE}`}>
                    <th scope="row" className="py-5 pr-6 text-left font-display text-lg font-semibold tracking-[-0.02em]">{r.project}</th>
                    <td className="py-5 pr-6 text-ink-muted tabular-nums">{r.budget}</td>
                    <td className="py-5 pr-6 text-ink-muted tabular-nums">{r.mid}</td>
                    <td className="py-5 px-5 text-white tabular-nums bg-violet/[0.07]">{r.ours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
        <p className="mt-6 text-xs text-ink-muted leading-relaxed max-w-[90ch]">{MARKET.note}</p>
      </div>
    </section>
  );
}

function Factors() {
  return (
    <section className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <SectionLabel num="04">Cost factors</SectionLabel>
            <Reveal><h2 className={`mt-7 ${H2_SM}`}>What changes the price</h2></Reveal>
          </div>
        </div>
        <ol className={`lg:col-span-8 border-t ${RULE}`}>
          {COST_FACTORS.map((f, i) => (
            <Reveal as="li" key={f.k} delay={i * 50} className={`border-b ${RULE}`}>
              <div className="grid grid-cols-[48px_1fr] md:grid-cols-[72px_minmax(0,0.9fr)_minmax(0,1.3fr)] gap-x-6 gap-y-3 py-8">
                <span className="nd-mono pt-1.5">{n2(i)}</span>
                <h3 className="font-display text-xl md:text-2xl font-semibold tracking-[-0.03em] leading-tight">{f.k}</h3>
                <p className="col-start-2 md:col-start-3 text-ink-muted leading-relaxed">{f.v}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

function PricingFaq() {
  return (
    <section className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <SectionLabel num="05">FAQ</SectionLabel>
            <Reveal><h2 className={`mt-7 ${H2_SM}`}>Pricing questions</h2></Reveal>
            <div className="mt-10 text-sm text-ink-muted leading-relaxed">
              Local pages:{' '}
              {CITIES.map((c, i) => (
                <React.Fragment key={c.slug}>
                  {i > 0 && ' · '}
                  <Link to={`${c.path}/`} className={LINK_CLS}>
                    {c.name}
                  </Link>
                </React.Fragment>
              ))}
            </div>
            <div className="mt-4 text-sm text-ink-muted">
              Building an app?{' '}
              <Link to="/mobile-app-development-cost/" className={LINK_CLS}>
                Mobile app development cost
              </Link>
            </div>
            <div className="mt-10">
              <MagneticButton href={SITE.whatsapp} external variant="primary">Get a fixed quote</MagneticButton>
            </div>
          </div>
        </div>
        <div className="lg:col-span-8">
          <FAQList items={PRICING_FAQ} testIdPrefix="pricing-faq" />
        </div>
      </div>
    </section>
  );
}

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <FaqSchema items={PRICING_FAQ} />
      <PricingHero />
      <Plans />
      <Retainers />
      <MarketTable />
      <Factors />
      <PricingFaq />
      <CTABand
        eyebrow="Fixed quote in 48 hours"
        headline="Tell us the scope. We’ll send a number."
        sub="A 30-minute call on WhatsApp is enough to scope most websites. You get a written, fixed quote — no hourly surprises."
      />
    </div>
  );
}
